import { c as _c } from "react/compiler-runtime";
import CarouselComponent, { CarouselType } from '@spa/components/carousel/carousel.component';
import ProductTileComponent, { PRODUCT_TILE_SIZES_CAROUSEL } from '@spa/components/product-list/product-tile/product-tile.component';
import { Props as ProductTileComponentProps, Product as ProductTileProduct } from '@spa/components/product-list/product-tile/product-tile.component';
import React, { FC, ReactElement, memo, useEffect, useRef, useState } from 'react';
import { SearchTrackingResponse, useSearchResultTracking } from '@spa/lib/tracking/useSearchResultTracking';
import VirtualizedCarouselComponent, { SizesPerBreakpointEntry as VirtualSizesPerBreakpointEntry } from '@spa/components/carousel/virtualized-carousel/virtualized-carousel.component';
import findProductCarouselByIdAction, { ProductCarouselPage } from '@spa/lib/actions/cms/findProductCarouselByIdAction';
import trackProductActions, { ProductActionsType, ProductsActionOrigin } from '@spa/lib/tracking/trackProductActions';
import { CarouselJsonLd } from 'next-seo';
import { CountryCodes } from '@spa/lib/const';
import HeadlineComponent from '@spa/components/headline/headline.component';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import { PageTemplate } from '@spa/lib/cms/const';
import { ProductTileContext } from '@spa/components/product-list/product-tile/product-tile.context';
import { ProductTileContextValue } from '@spa/components/product-list/product-tile/product-tile.context';
import { Props } from '@spa/components/carousel/product-carousel/product-carousel.component.props';
import { SessionsData } from '@spa/lib/auth/sessionTypes';
import { WishlistsActionOrigin } from '@spa/lib/tracking/trackWishlistsActions';
import { areEqual } from 'react-window';
import buildStructuredProductData from '@spa/lib/seo/buildStructuredProductData';
import getAuthSessionUserId from '@spa/lib/auth/getAuthSessionUserId';
import selectCountryCode from '@spa/lib/store/settings/selectors/selectCountryCode';
import selectGeneralSettings from '@spa/lib/store/settings/selectors/selectGeneralSettings';
import selectLastQueryMetadata from '@spa/lib/store/search/selectors/selectLastQueryMetadata';
import selectLocale from '@spa/lib/store/settings/selectors/selectLocale';
import selectPageTemplate from '@spa/lib/store/router/selectors/selectPageTemplate';
import selectPricesIncludeTax from '@spa/lib/store/settings/selectors/selectPricesIncludeTax';
import selectPublicBaseUrl from '@spa/lib/store/settings/selectors/selectPublicBaseUrl';
import selectSearchSettings from '@spa/lib/store/settings/selectors/selectSearchSettings';
import selectWishlistSettings from '@spa/lib/store/settings/selectors/selectWishlistSettings';
import styles from '@spa/components/carousel/product-carousel/product-carousel.component.module.scss';
import useFullPath from '@spa/lib/react/hooks/useFullPath';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
import useNextAuthOrSession from '@spa/lib/react/hooks/useNextAuthOrSession';
import { usePathname } from 'next/navigation';
import { usePopularityEventsAPI } from '@spa/lib/react/hooks/usePopularityEventsAPI';
import { useSelector } from 'react-redux';
export type Product = ProductTileProduct;
export type SizesPerBreakpointEntry = VirtualSizesPerBreakpointEntry;
export type TileProps = Omit<ProductTileComponentProps, 'onAddToCartClicked' | 'cart' | 'isAddingToCart'>;
interface ColumnData {
  readonly items: Array<Product | null>;
}
interface ColumnProps {
  readonly data: ColumnData;
  readonly index: number;
  readonly style: React.CSSProperties;
  readonly className?: string;
  readonly cssId?: string;
}
const MAX_CAROUSEL_PRODUCTS: number = 20;
const sizesPerBreakpointEntries: SizesPerBreakpointEntry[] = [{
  minViewportWidth: 0,
  height: 482,
  maxWidth: 768,
  itemWidth: 240
}, {
  minViewportWidth: 768,
  height: 550,
  maxWidth: 1024,
  itemWidth: 240
}, {
  minViewportWidth: 1024,
  height: 570,
  maxWidth: 1440,
  itemWidth: 280
}, {
  minViewportWidth: 1440,
  height: 570,
  maxWidth: 1440,
  itemWidth: 280
}];
const Column = t0 => {
  const $ = _c(7);
  const {
    data,
    index,
    style,
    className,
    cssId
  } = t0;
  const product = data.items[index];
  let t1;
  if ($[0] !== className || $[1] !== cssId || $[2] !== data.items.length || $[3] !== index || $[4] !== product || $[5] !== style) {
    t1 = product && <div style={{
      ...style
    }} key={product.code} className={className} id={`${cssId}${index}_`} role="group" aria-roledescription="slide" aria-label={`Slide ${index + 1} of ${data.items.length}`}><ProductTileComponent product={product} priority={index === 0} productActionOrigin={{
        originId: ProductsActionOrigin.PRODUCT_CAROUSEL,
        originType: ProductsActionOrigin.PRODUCT_CAROUSEL
      }} wishlistActionOrigin={{
        originId: WishlistsActionOrigin.PRODUCT_CAROUSEL,
        originType: WishlistsActionOrigin.PRODUCT_CAROUSEL
      }} productIndex={index} sizes={PRODUCT_TILE_SIZES_CAROUSEL} /></div>;
    $[0] = className;
    $[1] = cssId;
    $[2] = data.items.length;
    $[3] = index;
    $[4] = product;
    $[5] = style;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
};
const ColumnMemo = memo(Column, areEqual);
interface LoadedCarouselPage {
  readonly pageIndex: number;
  readonly result: ProductCarouselPage;
  readonly response: SearchTrackingResponse;
}
function CarouselPageTracking(t0) {
  const $ = _c(4);
  const {
    page,
    listId
  } = t0;
  let t1;
  if ($[0] !== listId || $[1] !== page.response || $[2] !== page.result.products) {
    t1 = {
      listId,
      products: page.result.products,
      response: page.response
    };
    $[0] = listId;
    $[1] = page.response;
    $[2] = page.result.products;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  useSearchResultTracking(t1);
  return null;
}
export default function productCarouselFactory(Tile: FC<TileProps>): (props: Props) => ReactElement {
  function ProductCarouselComponent(t0) {
    const $ = _c(85);
    const {
      products,
      title,
      componentId,
      totalCount,
      searchMetadata,
      pageSize,
      cmsPath,
      tabIndex,
      priority
    } = t0;
    const pathname = usePathname();
    const publicBaseUrl = useSelector(selectPublicBaseUrl);
    let t1;
    if ($[0] !== products || $[1] !== totalCount) {
      t1 = () => Array.from({
        length: totalCount
      }).map((_, index) => products[index] ?? null);
      $[0] = products;
      $[1] = totalCount;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    const [productsLoaded, setProductsLoaded] = useState(t1);
    let t2;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = [];
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    const [loadedPages, setLoadedPages] = useState(t2);
    let t3;
    if ($[4] !== pageSize || $[5] !== products.length || $[6] !== totalCount) {
      t3 = new Set(products.length >= Math.min(pageSize, totalCount) ? [0] : []);
      $[4] = pageSize;
      $[5] = products.length;
      $[6] = totalCount;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    const requestedPages = useRef(t3);
    const locale = useSelector(selectLocale);
    const country = useSelector(selectCountryCode);
    const shouldHidePrices = country === CountryCodes.HU;
    const lastQueryMetadata = useSelector(selectLastQueryMetadata);
    const pricesIncludeTax = useSelector(selectPricesIncludeTax);
    const pageTemplate = useSelector(selectPageTemplate);
    const {
      preOrderEnabled
    } = useSelector(selectGeneralSettings);
    const {
      popularityEventsEnabled
    } = useSelector(selectSearchSettings);
    const {
      plpEnabled: isWishlistEnabled
    } = useSelector(selectWishlistSettings);
    const {
      fullPath
    } = useFullPath();
    const namespacedIntl = useNamespacedIntl("product-tile");
    const {
      authSession,
      session
    } = useNextAuthOrSession();
    const {
      sendClickEvent
    } = usePopularityEventsAPI();
    const anonymousUserId = session.guid;
    let t4;
    if ($[8] !== authSession) {
      t4 = getAuthSessionUserId(authSession);
      $[8] = authSession;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    const userId = t4;
    let t5;
    if ($[10] !== loadedPages) {
      t5 = loadedPages.find(_temp);
      $[10] = loadedPages;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    const reloadedInitialPage = t5;
    const initialProducts = reloadedInitialPage?.result.products ?? products;
    const t6 = `karussell_${componentId}`;
    let t7;
    if ($[12] !== cmsPath || $[13] !== componentId || $[14] !== initialProducts.length || $[15] !== pageTemplate || $[16] !== pathname || $[17] !== reloadedInitialPage?.response || $[18] !== searchMetadata || $[19] !== tabIndex || $[20] !== totalCount) {
      t7 = initialProducts.length > 0 ? reloadedInitialPage?.response ?? {
        responseKey: JSON.stringify([cmsPath, componentId, tabIndex, 0]),
        totalCount,
        metadata: searchMetadata,
        pageTypeOptions: {
          pathname,
          pageTemplate
        }
      } : undefined;
      $[12] = cmsPath;
      $[13] = componentId;
      $[14] = initialProducts.length;
      $[15] = pageTemplate;
      $[16] = pathname;
      $[17] = reloadedInitialPage?.response;
      $[18] = searchMetadata;
      $[19] = tabIndex;
      $[20] = totalCount;
      $[21] = t7;
    } else {
      t7 = $[21];
    }
    let t8;
    if ($[22] !== initialProducts || $[23] !== t6 || $[24] !== t7) {
      t8 = {
        listId: t6,
        products: initialProducts,
        response: t7
      };
      $[22] = initialProducts;
      $[23] = t6;
      $[24] = t7;
      $[25] = t8;
    } else {
      t8 = $[25];
    }
    useSearchResultTracking(t8);
    let t9;
    if ($[26] !== authSession || $[27] !== session) {
      t9 = {
        authSession,
        session
      };
      $[26] = authSession;
      $[27] = session;
      $[28] = t9;
    } else {
      t9 = $[28];
    }
    let t10;
    if ($[29] !== country || $[30] !== fullPath || $[31] !== isWishlistEnabled || $[32] !== lastQueryMetadata || $[33] !== locale || $[34] !== namespacedIntl || $[35] !== pageTemplate || $[36] !== pathname || $[37] !== popularityEventsEnabled || $[38] !== preOrderEnabled || $[39] !== pricesIncludeTax || $[40] !== sendClickEvent || $[41] !== t9) {
      t10 = {
        countryCode: country,
        fullPath,
        isWishlistEnabled,
        lastQueryMetadata,
        locale,
        namespacedIntl,
        pageTemplate,
        pathname,
        popularityEventsEnabled,
        preOrderEnabled,
        pricesIncludeTax,
        sendClickEvent,
        sessionsData: t9
      };
      $[29] = country;
      $[30] = fullPath;
      $[31] = isWishlistEnabled;
      $[32] = lastQueryMetadata;
      $[33] = locale;
      $[34] = namespacedIntl;
      $[35] = pageTemplate;
      $[36] = pathname;
      $[37] = popularityEventsEnabled;
      $[38] = preOrderEnabled;
      $[39] = pricesIncludeTax;
      $[40] = sendClickEvent;
      $[41] = t9;
      $[42] = t10;
    } else {
      t10 = $[42];
    }
    const productTileContextValue = t10;
    let t11;
    let t12;
    if ($[43] !== anonymousUserId || $[44] !== lastQueryMetadata || $[45] !== pageTemplate || $[46] !== pathname || $[47] !== products || $[48] !== shouldHidePrices || $[49] !== userId) {
      t11 = () => () => {
        if (products.length) {
          trackProductActions({
            type: ProductActionsType.VIEW_SEARCH_ITEMS
          }, products, anonymousUserId, userId, {
            pathname,
            pageTemplate
          }, lastQueryMetadata, true, shouldHidePrices);
        }
      };
      t12 = [anonymousUserId, pageTemplate, lastQueryMetadata, pathname, products, shouldHidePrices, userId];
      $[43] = anonymousUserId;
      $[44] = lastQueryMetadata;
      $[45] = pageTemplate;
      $[46] = pathname;
      $[47] = products;
      $[48] = shouldHidePrices;
      $[49] = userId;
      $[50] = t11;
      $[51] = t12;
    } else {
      t11 = $[50];
      t12 = $[51];
    }
    useEffect(t11, t12);
    let t13;
    if ($[52] !== cmsPath || $[53] !== componentId || $[54] !== pageSize || $[55] !== pageTemplate || $[56] !== pathname || $[57] !== tabIndex) {
      const fetchPages = async (startIndex, stopIndex) => {
        if (pageSize > 0) {
          const firstPageIdx = Math.floor(startIndex / pageSize);
          const lastPageIdx = Math.floor(stopIndex / pageSize);
          const requests = [];
          for (let index_0 = firstPageIdx; index_0 <= lastPageIdx; index_0++) {
            const pageIndex = index_0;
            if (!requestedPages.current.has(pageIndex)) {
              requestedPages.current.add(pageIndex);
              requests.push(findProductCarouselByIdAction("findProductCarouselByIdAction", {
                pagePath: cmsPath,
                componentId,
                tabIndex,
                pagination: {
                  pageIndex,
                  pageSize
                }
              }).then(result => {
                const page_0 = {
                  pageIndex,
                  result,
                  response: {
                    responseKey: JSON.stringify([cmsPath, componentId, tabIndex, pageIndex]),
                    totalCount: result.totalCount,
                    metadata: result.searchMetadata,
                    pageTypeOptions: {
                      pathname,
                      pageTemplate
                    }
                  }
                };
                const expectedProducts = Math.min(pageSize, Math.max(0, result.totalCount - pageIndex * pageSize));
                if (result.products.length < expectedProducts) {
                  requestedPages.current.delete(pageIndex);
                }
                setProductsLoaded(previous => {
                  const newData = Array.from({
                    length: Math.min(previous.length, result.totalCount)
                  }, (__0, index_1) => previous[index_1] ?? null);
                  result.products.forEach((product, index_2) => {
                    const productIndex = pageIndex * pageSize + index_2;
                    if (productIndex < newData.length) {
                      newData[productIndex] = product;
                    }
                  });
                  return newData;
                });
                setLoadedPages(previous_0 => [...previous_0.filter(loadedPage => loadedPage.pageIndex !== pageIndex), page_0]);
              }).catch(error => {
                requestedPages.current.delete(pageIndex);
                throw error;
              }));
            }
          }
          if (requests.length > 0) {
            await Promise.all(requests);
          }
        }
      };
      t13 = async (startIndex_0, stopIndex_0) => {
        await fetchPages(startIndex_0, stopIndex_0);
      };
      $[52] = cmsPath;
      $[53] = componentId;
      $[54] = pageSize;
      $[55] = pageTemplate;
      $[56] = pathname;
      $[57] = tabIndex;
      $[58] = t13;
    } else {
      t13 = $[58];
    }
    const loadMoreItems = t13;
    let t14;
    if ($[59] !== productsLoaded) {
      t14 = index_3 => productsLoaded[index_3] !== null;
      $[59] = productsLoaded;
      $[60] = t14;
    } else {
      t14 = $[60];
    }
    const isItemLoaded = t14;
    const shouldVirtualize = totalCount >= 20;
    let t15;
    if ($[61] !== componentId || $[62] !== loadedPages) {
      let t16;
      if ($[64] !== componentId) {
        t16 = page_2 => <CarouselPageTracking key={page_2.pageIndex} page={page_2} listId={`karussell_${componentId}`} />;
        $[64] = componentId;
        $[65] = t16;
      } else {
        t16 = $[65];
      }
      t15 = loadedPages.filter(_temp2).map(t16);
      $[61] = componentId;
      $[62] = loadedPages;
      $[63] = t15;
    } else {
      t15 = $[63];
    }
    let t16;
    if ($[66] !== componentId || $[67] !== isItemLoaded || $[68] !== loadMoreItems || $[69] !== pathname || $[70] !== pricesIncludeTax || $[71] !== priority || $[72] !== products || $[73] !== productsLoaded || $[74] !== publicBaseUrl || $[75] !== shouldHidePrices || $[76] !== shouldVirtualize || $[77] !== title) {
      t16 = products.length > 0 && <section className={styles["product-carousel"]} data-component="ProductCarousel" aria-roledescription="carousel"><CarouselJsonLd useAppDir={true} ofType="product" url={`${publicBaseUrl}${pathname}`} data={buildStructuredProductData(products, publicBaseUrl, pricesIncludeTax, shouldHidePrices)} />{title && <HeadlineComponent id={`${componentId}-headline`} headline={title} subHeadline={null} link={null} headlineTag="h2" />}{shouldVirtualize ? <VirtualizedCarouselComponent componentId={componentId} sizesPerBreakpointEntries={sizesPerBreakpointEntries} items={productsLoaded} column={ColumnMemo} loadMoreItems={loadMoreItems} isItemLoaded={isItemLoaded} navigation={true} limit={MAX_CAROUSEL_PRODUCTS} title={title ?? undefined} /> : <CarouselComponent id={componentId} carouselType={CarouselType.PRODUCT} navigation={true} title={title ?? undefined}>{products.map((product_0, productIdx) => <div key={product_0.code} className={styles["product-carousel__tile"]}><Tile product={product_0} priority={priority && productIdx === 0} productActionOrigin={{
              originId: componentId,
              originType: ProductsActionOrigin.PRODUCT_CAROUSEL
            }} wishlistActionOrigin={{
              originId: componentId,
              originType: WishlistsActionOrigin.PRODUCT_CAROUSEL
            }} productIndex={productIdx} sizes={PRODUCT_TILE_SIZES_CAROUSEL} /></div>)}</CarouselComponent>}</section>;
      $[66] = componentId;
      $[67] = isItemLoaded;
      $[68] = loadMoreItems;
      $[69] = pathname;
      $[70] = pricesIncludeTax;
      $[71] = priority;
      $[72] = products;
      $[73] = productsLoaded;
      $[74] = publicBaseUrl;
      $[75] = shouldHidePrices;
      $[76] = shouldVirtualize;
      $[77] = title;
      $[78] = t16;
    } else {
      t16 = $[78];
    }
    let t17;
    if ($[79] !== t15 || $[80] !== t16) {
      t17 = <>{t15}{t16}</>;
      $[79] = t15;
      $[80] = t16;
      $[81] = t17;
    } else {
      t17 = $[81];
    }
    let t18;
    if ($[82] !== productTileContextValue || $[83] !== t17) {
      t18 = <ProductTileContext.Provider value={productTileContextValue}>{t17}</ProductTileContext.Provider>;
      $[82] = productTileContextValue;
      $[83] = t17;
      $[84] = t18;
    } else {
      t18 = $[84];
    }
    return t18;
  }
  function _temp2(page_1) {
    return page_1.pageIndex > 0 && page_1.result.products.length > 0;
  }
  function _temp(page) {
    return page.pageIndex === 0;
  }
  return ProductCarouselComponent;
}